import { ChangeDetectionStrategy, Component, Input, QueryList, SimpleChanges, ViewChild, ViewChildren } from "@angular/core";
import { trigger, state, style, transition, animate } from "@angular/animations";
import { CdkAccordionModule } from "@angular/cdk/accordion";
import { Store } from "@ngrx/store";
import moment from "moment";
import { NgScrollbar, NgScrollbarModule } from "ngx-scrollbar";
import { Observable, Subject } from "rxjs";
import { filter, map, skipWhile, take, takeUntil } from "rxjs/operators";
import * as ContactsActions from '../../contacts/store/contacts.actions';
import * as VisitorsActions from '../../inbox/store/visitors.actions';
import { Tag, User, Visitor } from "../../models";
import { selectAllContactTags, selectAllMembers, selectMemberById } from "../../settings/store/settings.selectors";
import * as TicketsActions from '../../inbox/store/tickets.actions';
import { AppState } from "../core.state";

import { CommonModule } from "@angular/common";
import { ReactiveFormsModule, UntypedFormControl } from "@angular/forms";
import { Update } from "@ngrx/entity";
import { cloneDeep, isNumber } from "lodash-es";
import { MediumComponent } from "../../inbox/status/medium.component";
import { StatusComponent } from "../../inbox/status/status.component";
import { AttachmentComponent } from '../../core/attachment/attachment.component';
import { MessageArticleComponent } from '../../core/message-article/message-article.component';
import { currentUser } from "../auth";
import { CharlaAvatarComponent } from "../avatar/avatar.component";
import { PriceCurrencyPipe } from "../pipes/currency.pipe";
import { CalendarDayPipe, DateFormatPipe, FromUtcPipe, LocalTimePipe, SessionDayPipe, TransformSQLDatePipe } from "../pipes/dates.pipe";
import { UniqueColorPipe } from "../pipes/ucolor.pipe";
import { EscapeHtmlPipe, ShortUrlPipe } from "../pipes/url.pipe";
import { TagEditorComponent } from "../tag-editor/tag-editor.component";
import { TimerComponent } from "../timer/timer.component";
import { WidgetFormField } from "src/app/models/widget-formfield.model";
import { selectPropertyById } from "../store/properties.selectors";
import { RocketEditComponent } from "../rocket-edit/rocket-edit.component";
import { MarkdownPipe } from "ngx-markdown";
import { InlineSVGComponent } from "../inline-svg/inline-svg.component";
import { InlineSVGDirective } from "../inline-svg/inline-svg.directive";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { TicketComponent } from "src/app/inbox/components/ticket/ticket.component";
import { CharlaTicketStatusComponent } from "src/app/inbox/components/ticket-status/status.component";
import { TimeAgoPipe } from "../pipes/timeago.pipe";
import { TicketsService } from "src/app/inbox/services/tickets.service";
import { selectTicketById } from "src/app/inbox/store/tickets.selectors";


@Component({
    selector: 'charla-contact-edit',
    templateUrl: './contact-edit.component.html',
    styleUrls: ['./contact-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('expandCollapse', [
            state('collapsed', style({ height: '0', overflow: 'hidden', opacity: 0 })),
            state('expanded', style({ height: '*', overflow: 'hidden', opacity: 1 })),
            transition('collapsed <=> expanded', animate('300ms ease-in-out'))
        ])
    ],
    imports: [CommonModule, ReactiveFormsModule, NgScrollbarModule, CdkAccordionModule,
    CharlaAvatarComponent, TagEditorComponent, TimerComponent, AttachmentComponent, MessageArticleComponent, StatusComponent, MediumComponent, RocketEditComponent,
    UniqueColorPipe, SessionDayPipe, CalendarDayPipe, TransformSQLDatePipe, PriceCurrencyPipe, ShortUrlPipe, EscapeHtmlPipe, DateFormatPipe, MarkdownPipe, InlineSVGComponent, InlineSVGDirective,
    CharlaTicketStatusComponent, FromUtcPipe, LocalTimePipe, TimeAgoPipe ]
})
export class ContactEditComponent{

    @Input() contact: Visitor; 
    @Input() show_conversations: boolean = true; 
    @Input() show_tickets: boolean = true; 
    @Input() use_store: string = "visitors";
    @Input() ticket_id: number;
	@ViewChildren('activities_elements') ac_elements: QueryList<any>;
	@ViewChild( NgScrollbar, { static: false }) messages_list: NgScrollbar;
    unsubscribe$ = new Subject();
    ro: ResizeObserver;
    start_from: any = 0;
    tags$: Observable<Tag[]>
    agents: User[];
    logged_user: User;
    show_add_note: boolean = false;
    addNoteControl: UntypedFormControl;
    custom_attributes$: Observable<WidgetFormField[]>;

    field_types = [ {id: 1, name: "text"}, {id: 2, name: "email"}, {id: 3, name: "single-select"}, 
        {id: 4, name: "multi-select"}, {id: 5, name: "number"}, {id: 6, name: "date"} ];

    constructor(
        private store: Store<AppState>,
        private offcanvasService: NgbOffcanvas,
        private ticketsService: TicketsService
    ){
        this.tags$ = this.store.select(selectAllContactTags);

        this.store.select(selectAllMembers).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(members => {
            
            this.agents = [];
            members.forEach(member => {
                this.agents.push({
                    id: member.id,
                    fullname: member.fullname,
                    email: member.email,
                    photo: member.photo ?? '/assets/media/users/default.jpg'
                });
            });
        });

		this.store.select(currentUser).pipe(
			filter( u => u != null),
			takeUntil(this.unsubscribe$)
		).subscribe( u => {
			this.logged_user = u;
		});

        this.addNoteControl = new UntypedFormControl('');

    }

    ngAfterViewInit(){

        if (this.show_conversations){
            this.ro = new ResizeObserver(entries => {
                console.log('Content changed:', entries[0].target.scrollHeight)
                this.messages_list.scrollTo({ bottom: 0, duration: 0});
            });
    
            if (this.messages_list) {
                // check if it's there in case there are no activities
                this.ro.observe( this.messages_list.nativeElement.getElementsByClassName("kt-chat__messages")[0]);
            }
    
        }

    }

    ngOnChanges(changes: SimpleChanges) {
        
        console.log("Contact Changes ---");
        var current_session_created = changes["contact"].currentValue?.current_session_created;
        
        if (current_session_created) {
            let n = moment.utc();
            let sd =  moment.utc( current_session_created.replace('T', ' '), 'YYYY-MM-DD HH:mm:ss');
            this.start_from = Math.abs( n.diff(sd, 'seconds') );
        } else {
            this.start_from = '';
        }

        if (changes["contact"]){
            this.custom_attributes$ = this.store.select(selectPropertyById({property_id: this.contact.property_id})).pipe(
                filter( p => p != undefined),
                map ( p => p.visitor_attributes)
            );
        }
        
      
    }
        
    save_field(field: string | number, value: string, extra?: number){

        var entity_update: Update<Visitor>;
        if (field == "email"){
            entity_update = {
                id: this.contact.id,
                changes: { email: value }
            };
        } else if (field == "phone"){
            entity_update = {
                id: this.contact.id,
                changes: { phone: value }
            };
        } else if (field == "name"){
            entity_update = {
                id: this.contact.id,
                changes: { name: value }
            };
        } else if (field == "note"){
            var notes = cloneDeep(this.contact.notes);
            var note = notes.find(q => q.id == extra);
            note.note = value;

            entity_update = {
                id: this.contact.id,
                changes: { notes: notes }
            };
        } else if ( isNumber(field) ){
            // this is a custom attribute
            const atts = cloneDeep(this.contact.custom_attributes) || [];
            const att = atts.find(q => q.id == field);
            if (att){
                // update the existing attribute
                att.value = value.toString();
            } else {
                // add a new 
                atts.push({ id: field, value: value.toString() });
            }
            entity_update = {
                id: this.contact.id,
                changes: { custom_attributes: atts }
            };
        }

        if (this.use_store == "contacts"){
            this.store.dispatch( ContactsActions.ContactSaveRequest( {contact: entity_update}));	
        } else if (this.use_store == "visitors"){
            this.store.dispatch( VisitorsActions.VisitorInfoSaveRequest( {visitor: entity_update}));	
        } else if (this.use_store == "tickets"){
            this.store.dispatch( TicketsActions.TicketContactSaveRequest( {contact: entity_update, ticket_id: this.ticket_id}));	
        }


	}

	getAgentPhoto(agent: string){
		
		return this.store.select(selectMemberById({member_id: agent})).pipe(
			skipWhile(ag => (ag == undefined)),
            take(1),
			map(ag => ag.photo)
		);

	}

    getAttributeValue(attribute_definition_id: number, contact_attributes: WidgetFormField[]){

        return contact_attributes?.find(q => q.id == attribute_definition_id)?.value ?? "";

    }

    getFieldMode(field_type: number){
        return this.field_types.find(q => q.id == field_type)?.name ?? "text";
    }

    get_agent(id: string){
        return this.agents.find(q => q.id == id);
    }

	save_tags(values: Tag[]){
        
        var entity_update: Update<Visitor> = {
            id: this.contact.id,
            changes: { tags: values }
        };

        if (this.use_store == "contacts"){
            this.store.dispatch( ContactsActions.ContactSaveRequest( {contact: entity_update}));	
        } else if (this.use_store == "visitors"){
            this.store.dispatch( VisitorsActions.VisitorInfoSaveRequest( {visitor: entity_update}));	
        } else if (this.use_store == "tickets"){
            this.store.dispatch( TicketsActions.TicketContactSaveRequest( {contact: entity_update, ticket_id: this.ticket_id}));	
        }
	
    }

    add_note(){

        var notes = cloneDeep(this.contact.notes);
        let note = this.addNoteControl.value;
        notes.push({ note:note, id: 0 });
        
        var entity_update: Update<Visitor> = {
            id: this.contact.id,
            changes: { notes: notes }
        };

        if (this.use_store == "contacts"){
            this.store.dispatch( ContactsActions.ContactSaveRequest( {contact: entity_update}));	
        } else if (this.use_store == "visitors"){
            this.store.dispatch( VisitorsActions.VisitorInfoSaveRequest( {visitor: entity_update}));	
        } else if (this.use_store == "tickets"){
            this.store.dispatch( TicketsActions.TicketContactSaveRequest( {contact: entity_update, ticket_id: this.ticket_id}));	
        }

        this.show_add_note = false;

    }

    delete_note(id: number){
       
        var notes = cloneDeep(this.contact.notes);
        notes = notes.filter(q => q.id != id);

        var entity_update: Update<Visitor> = {
            id: this.contact.id,
            changes: { notes: notes }
        };

        if (this.use_store == "contacts"){
            this.store.dispatch( ContactsActions.ContactSaveRequest( {contact: entity_update}));	
        } else if (this.use_store == "visitors"){
            this.store.dispatch( VisitorsActions.VisitorInfoSaveRequest( {visitor: entity_update}));	
        } else if (this.use_store == "tickets"){
            this.store.dispatch( TicketsActions.TicketContactSaveRequest( {contact: entity_update, ticket_id: this.ticket_id}));	
        }

    }

    open_ticket(ticket_id: number){

        this.store.dispatch(TicketsActions.OpenTicketRequested({ ticket_id: ticket_id })); 

    }


	trackByFn(index, item) {    
		return item.activity_id
	}

    ngOnDestroy() {
        this.ro?.disconnect();
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

}